import { resolveDeployment } from './deployment';

/* ══════════════════════════════════════════════════════════════════════════
 * Third-party cookie consent (Cookie-Script), one banner per brand family.
 *
 *   CLS (cls-dk, cls-se, cls-fi) → clseifert.com's Cookie-Script banner
 *   ABC (abc-se)                 → abcgruppen.se's Cookie-Script banner
 *
 * Upstream rule: a visitor arriving FROM the brand's core website (referrer
 * host is, or is a subdomain of, abcgruppen.se / clseifert.com) has already
 * seen that site's banner, so the script is not loaded at all. The browser
 * only exposes the page that loaded the document (`document.referrer`), and
 * that page is the storefront itself after any full reload — so the upstream
 * verdict is remembered per tab (sessionStorage) for the rest of the visit.
 *
 * The storefront is itself served on those domains (www.abcgruppen.se/merch,
 * clseifert.com/{dk,sv}/merch, the DEV host fe1487.clseifert.com), so a
 * referrer that is a STOREFRONT page never counts as the core website.
 *
 * Shown once: Cookie-Script stores the visitor's choice in its own
 * `CookieScriptConsent` cookie, but each script is configured (Cookie-Script
 * dashboard) with a FIXED cookie domain — `clseifert.com` / `abcgruppen.se`.
 * On any other host (localhost, ABC on the DEV host fe1487.clseifert.com) the
 * browser rejects that cookie, so the banner reappeared on every reload. On
 * those hosts the choice is recorded here instead (localStorage, same 30-day
 * lifetime as the vendor cookie) and the script is not loaded again. On the
 * family's own domain the vendor cookie works, so the script keeps loading on
 * every page (it enforces the consent and shows no banner once chosen).
 * ══════════════════════════════════════════════════════════════════════════ */

export const COOKIE_CONSENT_BRANDS = {
  CLS: {
    domain: 'clseifert.com',
    src: 'https://cdn.cookie-script.com/s/f784410bf912fb3782220a6f582e55a2.js',
  },
  ABC: {
    domain: 'abcgruppen.se',
    src: 'https://cdn.cookie-script.com/s/cee25fba8b8256794e3bd5ceee10956e.js',
  },
};

const UPSTREAM_KEY = (family) => `cookie_consent_upstream_${family}`;
const CHOICE_KEY = (family) => `cookie_consent_choice_${family}`;
const SCRIPT_ID = 'cookie-script-consent';
/** Lifetime of a locally recorded choice — matches the vendor cookie (30 days). */
export const CONSENT_CHOICE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Cookie-Script events (dispatched on `document`) that end the banner. */
const CHOICE_EVENTS = ['CookieScriptAccept', 'CookieScriptAcceptAll', 'CookieScriptReject', 'CookieScriptClose'];

/** Internal `/clothing/{brand}` pathname → 'CLS' | 'ABC' | null. */
export function consentFamilyForPath(pathname) {
  const match = /^\/clothing\/([a-z]+)-[a-z]+(?:\/|$)/.exec(String(pathname || ''));
  if (!match) return null;
  if (match[1] === 'abc') return 'ABC';
  if (match[1] === 'cls') return 'CLS';
  return null;
}

function hostMatches(host, domain) {
  return host === domain || host.endsWith(`.${domain}`);
}

/** True when the URL is a storefront page (public sub-path or internal route). */
function isStorefrontUrl(url) {
  if (resolveDeployment(url.hostname, url.pathname)) return true;
  return url.pathname === '/clothing' || url.pathname.startsWith('/clothing/');
}

/**
 * True when `referrer` is a page of the family's core website. A cross-origin
 * referrer usually carries the origin only (`strict-origin-when-cross-origin`),
 * which is enough: the host decides, and a bare origin is never a storefront.
 */
export function cameFromCoreSite(referrer, family) {
  const brand = COOKIE_CONSENT_BRANDS[family];
  if (!brand || !referrer) return false;
  let url;
  try {
    url = new URL(referrer);
  } catch {
    return false;
  }
  if (!hostMatches(url.hostname.toLowerCase(), brand.domain)) return false;
  return !isStorefrontUrl(url);
}

/**
 * True when Cookie-Script can store its consent cookie on this host — i.e. the
 * host is the family's configured cookie domain or a subdomain of it.
 */
export function vendorCookieWorks(hostname, family) {
  const brand = COOKIE_CONSENT_BRANDS[family];
  return Boolean(brand) && hostMatches(String(hostname || '').toLowerCase(), brand.domain);
}

function readLocalChoice(family) {
  try {
    const t = Number(localStorage.getItem(CHOICE_KEY(family)));
    return Number.isFinite(t) && t > 0 && Date.now() - t < CONSENT_CHOICE_TTL_MS;
  } catch {
    return false;
  }
}

let stopListening = null;

/** Record the visitor's next choice for `family` (one listener at a time). */
function rememberLocalChoice(family) {
  if (stopListening) stopListening();
  const save = () => {
    try {
      localStorage.setItem(CHOICE_KEY(family), String(Date.now()));
    } catch {
      /* storage blocked — the banner simply shows again next time */
    }
    stopListening();
  };
  stopListening = () => {
    CHOICE_EVENTS.forEach((name) => document.removeEventListener(name, save));
    stopListening = null;
  };
  CHOICE_EVENTS.forEach((name) => document.addEventListener(name, save));
}

function readUpstream(family) {
  try {
    return sessionStorage.getItem(UPSTREAM_KEY(family)) === '1';
  } catch {
    return false;
  }
}

function writeUpstream(family) {
  try {
    sessionStorage.setItem(UPSTREAM_KEY(family), '1');
  } catch {
    /* storage blocked — the referrer check still covers this page load */
  }
}

/**
 * Load the brand family's consent banner for the current route, unless the
 * visitor came from that family's core website. Idempotent: the script is
 * injected at most once per page load (a dev-only cross-brand SPA switch keeps
 * the first family's banner — Cookie-Script cannot be unloaded).
 */
export function ensureCookieConsent(pathname) {
  if (typeof document === 'undefined') return;
  const family = consentFamilyForPath(pathname);
  if (!family) return;
  if (document.getElementById(SCRIPT_ID)) return;

  if (readUpstream(family)) return;
  if (cameFromCoreSite(document.referrer, family)) {
    writeUpstream(family);
    return;
  }

  const localFallback = !vendorCookieWorks(window.location.hostname, family);
  if (localFallback) {
    if (readLocalChoice(family)) return;
    rememberLocalChoice(family);
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.type = 'text/javascript';
  script.charset = 'UTF-8';
  script.src = COOKIE_CONSENT_BRANDS[family].src;
  document.head.appendChild(script);
}
